import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Save, CheckCircle2, AlertTriangle, Filter, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function ProductMapping() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending"); // 'all' | 'pending' | 'mapped'
  const [edits, setEdits] = useState({}); // { [productId]: { friendly_name, category, sub_category, color, storage } }

  // Load all products from backend
  const { data: products = [], isLoading, refetch } = useQuery({
    queryKey: ["product-mapping-list"],
    queryFn: async () => {
      const { data } = await api.get("/products");
      return data;
    },
  });

  // Dynamically derive existing categories and series from the live database
  const existingCategories = useMemo(() => {
    const defaultCats = ["smartphones", "tablets", "watches", "accessories", "audio"];
    const fromData = products.map(p => p.category?.toLowerCase()).filter(Boolean);
    return Array.from(new Set([...defaultCats, ...fromData]));
  }, [products]);

  const existingSeries = useMemo(() => {
    const defaultSeries = [
      "Galaxy S Series",
      "Galaxy Z Series",
      "Galaxy A Series",
      "Galaxy M Series",
      "Galaxy F Series",
      "Galaxy Tab S Series",
      "Galaxy Tab A Series",
      "Galaxy Watch",
      "Galaxy Buds",
      "Samsung Accessories",
    ];
    const fromData = products.map(p => p.sub_category).filter(Boolean);
    return Array.from(new Set([...defaultSeries, ...fromData]));
  }, [products]);

  // Smart Samsung SAP Description Parser
  // Eliminates external scraping by intelligently extracting model, series, storage, RAM, and color from raw SAP strings
  const parseSapDescription = (rawCode = "", rawDesc = "") => {
    const desc = rawDesc || "";
    let friendlyName = desc;
    let category = "smartphones";
    let subCategory = "Galaxy A Series";
    let ram = "";
    let storage = "";
    let color = "";

    // 1. Detect Category
    const lower = desc.toLowerCase();
    if (lower.includes("tab") || lower.includes("tablet")) {
      category = "tablets";
    } else if (lower.includes("watch") || lower.includes("gear") || lower.includes("fit")) {
      category = "watches";
    } else if (lower.includes("buds") || lower.includes("headphone") || lower.includes("audio")) {
      category = "audio";
    } else if (lower.includes("cover") || lower.includes("case") || lower.includes("adapter") || lower.includes("cable") || lower.includes("s-pen")) {
      category = "accessories";
    } else {
      category = "smartphones";
    }

    // 2. Detect Series / Sub-Category
    if (category === "smartphones") {
      if (/galaxy\s*s\d+/i.test(desc) || /s\d{2}/i.test(desc)) subCategory = "Galaxy S Series";
      else if (/galaxy\s*z/i.test(desc) || /fold|flip/i.test(desc)) subCategory = "Galaxy Z Series";
      else if (/galaxy\s*a\d+/i.test(desc) || /a\d{2}/i.test(desc)) subCategory = "Galaxy A Series";
      else if (/galaxy\s*m\d+/i.test(desc) || /m\d{2}/i.test(desc)) subCategory = "Galaxy M Series";
      else if (/galaxy\s*f\d+/i.test(desc) || /f\d{2}/i.test(desc)) subCategory = "Galaxy F Series";
    } else if (category === "tablets") {
      if (/tab\s*s/i.test(desc)) subCategory = "Galaxy Tab S Series";
      else subCategory = "Galaxy Tab A Series";
    } else if (category === "watches") {
      subCategory = "Galaxy Watch";
    } else if (category === "audio") {
      subCategory = "Galaxy Buds";
    } else {
      subCategory = "Samsung Accessories";
    }

    // 3. Extract Storage & RAM separately (only when explicitly in description)
    const slashMatch = desc.match(/(\d+)\s*\/\s*(\d+)\s*(GB|TB)?/i);
    if (slashMatch) {
      ram = `${slashMatch[1]}GB`;
      const rom = slashMatch[2];
      const unit = slashMatch[3] ? slashMatch[3].toUpperCase() : (parseInt(rom, 10) <= 2 ? "TB" : "GB");
      storage = `${rom}${unit}`;
    } else {
      const ramExplicit = desc.match(/(\d+)\s*GB\s*(?:RAM|Memory)/i) || desc.match(/\((\d+)\s*GB\s*(?:RAM|Memory)?\)/i);
      const romExplicit = desc.match(/(\d+)\s*(GB|TB)(?!\s*RAM|\s*Memory)/i);

      if (ramExplicit) {
        ram = `${ramExplicit[1]}GB`;
      }
      if (romExplicit) {
        storage = `${romExplicit[1]}${romExplicit[2].toUpperCase()}`;
      }
    }

    // 4. Extract Common Samsung Color Names (Never guess or default)
    const colorsList = [
      "Titanium Whitesilver", "Titanium Gray", "Titanium Black", "Titanium Violet", "Titanium Yellow", "Titanium Silver",
      "Onyx Black", "Marble Gray", "Cobalt Violet", "Amber Yellow", "Jade Green", "Sandstone Orange",
      "Phantom Black", "Phantom Silver", "Cream", "Green", "Lavender", "Graphite", "Mint",
      "Awesome Navy", "Awesome Iceblue", "Awesome Lilac", "Awesome Lemon", "Awesome Violet",
      "Light Green", "Light Gray", "Light Violet", "Dark Red", "Silver", "Black", "White", "Blue", "Gold", "Pink", "Yellow", "Gray"
    ];
    for (const c of colorsList) {
      const regex = new RegExp(`(?:-|\\b)${c}(?:\\b|$)`, 'i');
      if (regex.test(desc)) {
        color = c;
        break;
      }
    }

    // 5. Clean Friendly Name
    let clean = desc
      .replace(/\/MOBILE\s*PHONE/gi, '')
      .replace(/MOBILE\s*PHONE/gi, '')
      .replace(/\/Travel\s*Adaptor/gi, '')
      .replace(/\/Power\s*Bank/gi, '')
      .replace(/\/Tab-Calling/gi, '')
      .replace(/\/Buds/gi, '')
      .replace(/SM-[A-Z0-9]+\/?/gi, '')
      .replace(/\s+/g, ' ')
      .trim();

    if (clean.length > 3) {
      friendlyName = clean;
    }

    return { friendly_name: friendlyName, category, sub_category: subCategory, ram, color, storage };
  };

  const handleAutoParseRow = (p) => {
    const parsed = parseSapDescription(p.sap_item_code || p.sku, p.name);
    setEdits(prev => {
      const current = prev[p.product_id] || {};
      return {
        ...prev,
        [p.product_id]: {
          friendly_name: current.friendly_name || p.friendly_name || parsed.friendly_name,
          category: current.category || p.category || parsed.category,
          sub_category: current.sub_category || p.sub_category || parsed.sub_category,
          ram: current.ram || p.ram || parsed.ram,
          color: current.color || p.color || parsed.color,
          storage: current.storage || p.storage || parsed.storage,
        }
      };
    });
  };

  const handleAutoParseAllPage = () => {
    let count = 0;
    const payload = [];

    paginatedItems.forEach(p => {
      const parsed = parseSapDescription(p.sap_item_code || p.sku, p.name);
      const current = edits[p.product_id] || {};
      
      const friendlyVal = current.friendly_name !== undefined ? current.friendly_name : (p.friendly_name || parsed.friendly_name);
      const catVal = current.category !== undefined ? current.category : (p.category || parsed.category);
      const subCatVal = current.sub_category !== undefined ? current.sub_category : (p.sub_category || parsed.sub_category);
      const ramVal = current.ram !== undefined ? current.ram : (p.ram || parsed.ram);
      const colorVal = current.color !== undefined ? current.color : (p.color || parsed.color);
      const storageVal = current.storage !== undefined ? current.storage : (p.storage || parsed.storage);

      payload.push({
        product_id: p.product_id,
        sap_item_code: p.sap_item_code || p.sku,
        friendly_name: friendlyVal,
        category: catVal,
        sub_category: subCatVal,
        color: colorVal,
        ram: formatUnit(ramVal, "GB"),
        storage: formatUnit(storageVal, "GB"),
      });
      count++;
    });

    if (payload.length === 0) {
      return toast.info("No items on this page to auto-suggest.");
    }

    toast.loading("Auto-suggesting and saving mappings...");
    saveMutation.mutate(payload, {
      onSuccess: () => {
        toast.dismiss();
        toast.success(`Auto-mapped and saved ${count} items! Filter refreshed.`);
      }
    });
  };

  const handleEditChange = (productId, field, value) => {
    setEdits(prev => ({
      ...prev,
      [productId]: {
        ...(prev[productId] || {}),
        [field]: value,
      }
    }));
  };

  const [page, setPage] = useState(1);
  const PAGE_SIZE = 100;

  const [stockOnly, setStockOnly] = useState(true); // Default to in-stock only to ignore legacy end-of-life SKUs

  // Filtered rows
  const filtered = useMemo(() => {
    return products.filter(p => {
      const stock = parseInt(p.stock || p.available_stock || 0, 10);
      if (stockOnly && stock <= 0) return false;

      const isPending = !p.category || (p.category !== "accessories" && p.category !== "audio" && !p.color) || !p.friendly_name || (p.category !== "accessories" && p.category !== "audio" && !p.storage);
      if (statusFilter === "pending" && !isPending) return false;
      if (statusFilter === "mapped" && isPending) return false;

      if (search) {
        const term = search.toLowerCase();
        const code = (p.sap_item_code || p.sku || "").toLowerCase();
        const name = (p.name || "").toLowerCase();
        const friendly = (p.friendly_name || "").toLowerCase();
        return code.includes(term) || name.includes(term) || friendly.includes(term);
      }
      return true;
    });
  }, [products, statusFilter, search, stockOnly]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE) || 1;
  const paginatedItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, search, stockOnly]);

  const pendingCount = useMemo(() => {
    return products.filter(p => {
      const stock = parseInt(p.stock || p.available_stock || 0, 10);
      if (stockOnly && stock <= 0) return false;
      return !p.category || (p.category !== "accessories" && p.category !== "audio" && !p.color) || !p.friendly_name || (p.category !== "accessories" && p.category !== "audio" && !p.storage);
    }).length;
  }, [products, stockOnly]);

  // Save batch mutation
  const saveMutation = useMutation({
    mutationFn: async (payload) => {
      const { data } = await api.post("/product-mapping/batch", { items: payload });
      return { data, payload };
    },
    onSuccess: ({ data, payload }) => {
      toast.success(`Successfully saved ${data.updated_count || payload.length} product mapping(s)!`);
      
      // Optimistically update products cache in React Query
      queryClient.setQueryData(["product-mapping-list"], (old = []) => {
        const updateMap = {};
        payload.forEach(item => {
          updateMap[item.product_id] = item;
        });
        return old.map(p => {
          if (updateMap[p.product_id]) {
            return {
              ...p,
              ...updateMap[p.product_id]
            };
          }
          return p;
        });
      });

      // Clear local row edits for saved items
      setEdits(prev => {
        const next = { ...prev };
        payload.forEach(item => {
          delete next[item.product_id];
        });
        return next;
      });

      queryClient.invalidateQueries(["products"]);
      queryClient.invalidateQueries(["unmapped-skus-count"]);
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || "Failed to save product mappings");
    }
  });

  const formatUnit = (val, defaultUnit = "GB") => {
    if (!val) return "";
    const clean = String(val).trim();
    if (!clean) return "";
    if (/^\d+$/.test(clean)) {
      if (clean === "1" || clean === "2") return `${clean}TB`;
      return `${clean}${defaultUnit}`;
    }
    return clean.replace(/\s+/g, "").toUpperCase();
  };

  const handleSaveAll = () => {
    const editKeys = Object.keys(edits);
    if (editKeys.length === 0) {
      return toast.info("No edits made yet.");
    }
    const payload = editKeys.map(id => {
      const original = products.find(p => p.product_id === id) || {};
      const rawRam = edits[id].ram !== undefined ? edits[id].ram : original.ram;
      const rawStorage = edits[id].storage !== undefined ? edits[id].storage : original.storage;

      return {
        product_id: id,
        sap_item_code: original.sap_item_code || original.sku,
        friendly_name: edits[id].friendly_name !== undefined ? edits[id].friendly_name : (original.friendly_name || original.name),
        category: edits[id].category !== undefined ? edits[id].category : original.category,
        sub_category: edits[id].sub_category !== undefined ? edits[id].sub_category : original.sub_category,
        color: edits[id].color !== undefined ? edits[id].color : original.color,
        ram: formatUnit(rawRam, "GB"),
        storage: formatUnit(rawStorage, "GB"),
      };
    });
    saveMutation.mutate(payload);
  };

  const handleSingleSave = (p) => {
    const itemEdits = edits[p.product_id] || {};
    const rawRam = itemEdits.ram !== undefined ? itemEdits.ram : p.ram;
    const rawStorage = itemEdits.storage !== undefined ? itemEdits.storage : p.storage;

    const payload = [{
      product_id: p.product_id,
      sap_item_code: p.sap_item_code || p.sku,
      friendly_name: itemEdits.friendly_name !== undefined ? itemEdits.friendly_name : (p.friendly_name || p.name),
      category: itemEdits.category !== undefined ? itemEdits.category : p.category,
      sub_category: itemEdits.sub_category !== undefined ? itemEdits.sub_category : p.sub_category,
      color: itemEdits.color !== undefined ? itemEdits.color : p.color,
      ram: formatUnit(rawRam, "GB"),
      storage: formatUnit(rawStorage, "GB"),
    }];
    saveMutation.mutate(payload);
  };

  const hasEdits = Object.keys(edits).length > 0;

  return (
    <div className="space-y-6" data-testid="product-mapping-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500">Accounts & Inventory Hub</div>
          <h1 className="mt-1 text-3xl font-medium tracking-tight" style={{ fontFamily: "'General Sans', system-ui, sans-serif" }}>
            SAP Product Metadata Mapping
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Map raw SAP SKUs into dealer-friendly categories, series, colors, and storage specifications.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => refetch()} className="h-9">
            <RefreshCw size={14} className="mr-1.5" /> Refresh
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleAutoParseAllPage} 
            className="h-9 border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 font-medium"
            title="Auto-extract friendly names, colors, series, and storage from raw SAP codes for this page"
          >
            <Sparkles size={14} className="mr-1.5 text-blue-600" /> Auto-Suggest (This Page)
          </Button>
          <Button 
            onClick={handleSaveAll} 
            disabled={!hasEdits || saveMutation.isPending} 
            className="bg-[#0055FF] hover:bg-[#0044CC] h-9"
          >
            <Save size={14} className="mr-1.5" /> 
            {saveMutation.isPending ? "Saving..." : `Save Changes (${Object.keys(edits).length})`}
          </Button>
        </div>
      </div>

      {/* Persistent Alert Banner if Pending Items exist */}
      {pendingCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center shrink-0 text-amber-700">
              <AlertTriangle size={18} />
            </div>
            <div>
              <div className="text-sm font-semibold text-amber-900">
                {pendingCount} Product{pendingCount !== 1 ? "s" : ""} require metadata mapping
              </div>
              <div className="text-xs text-amber-700 mt-0.5">
                These items are currently available in the catalogue under raw SAP codes. Completing friendly metadata helps dealers find items quickly.
              </div>
            </div>
          </div>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => setStatusFilter("pending")}
            className="bg-white border-amber-300 text-amber-800 hover:bg-amber-100 shrink-0"
          >
            Show Pending Only
          </Button>
        </div>
      )}

      {/* Filter and Search Bar */}
      <Card className="p-4 bg-white border border-slate-200">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-[280px]">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
              <Input
                placeholder="Search by SAP Code, Name, or Friendly Name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-9 text-sm"
              />
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setStockOnly(!stockOnly)}
              className={`h-8 text-xs font-medium ${stockOnly ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100" : "bg-white text-slate-500 hover:bg-slate-50"}`}
            >
              {stockOnly ? "✓ In-Stock Items Only" : "Include Zero Stock / EOL SKUs"}
            </Button>
            <div className="flex items-center gap-2">
              <Filter size={14} className="text-slate-400" />
              <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5 text-xs">
                <button
                  onClick={() => setStatusFilter("all")}
                  className={`px-3 py-1 rounded-sm font-medium transition-colors ${statusFilter === "all" ? "bg-white text-slate-900 shadow-xs" : "text-slate-600 hover:text-slate-900"}`}
                >
                  All ({filtered.length})
                </button>
                <button
                  onClick={() => setStatusFilter("pending")}
                  className={`px-3 py-1 rounded-sm font-medium transition-colors ${statusFilter === "pending" ? "bg-amber-500 text-white shadow-xs" : "text-slate-600 hover:text-slate-900"}`}
                >
                  Pending ({pendingCount})
                </button>
                <button
                  onClick={() => setStatusFilter("mapped")}
                  className={`px-3 py-1 rounded-sm font-medium transition-colors ${statusFilter === "mapped" ? "bg-emerald-600 text-white shadow-xs" : "text-slate-600 hover:text-slate-900"}`}
                >
                  Mapped ({filtered.length - pendingCount})
                </button>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Mapping Table */}
      <Card className="overflow-hidden border border-slate-200 shadow-xs">
        <div className="overflow-x-auto max-h-[calc(100vh-320px)] overflow-y-auto">
          <table className="w-full text-sm border-collapse min-w-[1300px]">
            <thead className="sticky top-0 z-10 bg-slate-100 text-[10px] uppercase tracking-wider text-slate-600 border-b border-slate-200">
              <tr>
                <th className="text-left px-3 py-3 min-w-[170px]">SAP Item Code</th>
                <th className="text-left px-3 py-3 min-w-[200px]">SAP Description</th>
                <th className="text-left px-3 py-3 min-w-[220px]">Friendly Name (Dealer Facing)</th>
                <th className="text-left px-3 py-3 min-w-[140px]">Category</th>
                <th className="text-left px-3 py-3 min-w-[160px]">Series / Sub-Cat</th>
                <th className="text-left px-3 py-3 min-w-[130px]">Color</th>
                <th className="text-left px-3 py-3 min-w-[90px]">Storage</th>
                <th className="text-left px-3 py-3 min-w-[80px]">RAM</th>
                <th className="text-right px-3 py-3 min-w-[110px]">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                <tr>
                  <td colSpan={9} className="text-center py-12 text-slate-500">Loading products...</td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-12 text-slate-500">No products match the selected criteria.</td>
                </tr>
              ) : (
                paginatedItems.map(p => {
                  const currentEdit = edits[p.product_id] || {};
                  const friendlyVal = currentEdit.friendly_name !== undefined ? currentEdit.friendly_name : (p.friendly_name || p.name);
                  const catVal = currentEdit.category !== undefined ? currentEdit.category : (p.category || "smartphones");
                  const subCatVal = currentEdit.sub_category !== undefined ? currentEdit.sub_category : (p.sub_category || "");
                  const colorVal = currentEdit.color !== undefined ? currentEdit.color : (p.color || "");
                  const ramVal = currentEdit.ram !== undefined ? currentEdit.ram : (p.ram || "");
                  const storageVal = currentEdit.storage !== undefined ? currentEdit.storage : (p.storage || "");
                  const isRowEdited = !!edits[p.product_id];

                  return (
                    <tr key={p.product_id} className={`hover:bg-slate-50 transition-colors ${isRowEdited ? "bg-blue-50/50" : ""}`}>
                      <td className="px-3 py-2.5 font-mono text-xs text-slate-700 font-medium whitespace-nowrap">
                        {p.sap_item_code || p.sku}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-slate-600 font-mono leading-tight whitespace-normal break-words">
                        {p.name}
                      </td>
                      <td className="px-3 py-2.5">
                        <Input
                          value={friendlyVal}
                          onChange={(e) => handleEditChange(p.product_id, "friendly_name", e.target.value)}
                          className="h-8 text-xs min-w-[200px]"
                          placeholder="e.g. Galaxy S24 Ultra (512 GB)"
                        />
                      </td>
                      <td className="px-3 py-2.5">
                        <input
                          list="category-suggestions"
                          value={catVal}
                          onChange={(e) => handleEditChange(p.product_id, "category", e.target.value)}
                          className="h-8 text-xs w-full min-w-[130px] px-2.5 py-1 border border-slate-200 rounded-md bg-white text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Type / select category..."
                        />
                      </td>
                      <td className="px-3 py-2.5">
                        <input
                          list="series-suggestions"
                          value={subCatVal}
                          onChange={(e) => handleEditChange(p.product_id, "sub_category", e.target.value)}
                          className="h-8 text-xs w-full min-w-[150px] px-2.5 py-1 border border-slate-200 rounded-md bg-white text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Type / select series..."
                        />
                      </td>
                      <td className="px-3 py-2.5">
                        <Input
                          value={colorVal}
                          onChange={(e) => handleEditChange(p.product_id, "color", e.target.value)}
                          className="h-8 text-xs min-w-[110px]"
                          placeholder="Titanium Gray"
                        />
                      </td>
                      <td className="px-3 py-2.5">
                        <Input
                          value={storageVal}
                          onChange={(e) => handleEditChange(p.product_id, "storage", e.target.value)}
                          className="h-8 text-xs min-w-[75px]"
                          placeholder="512GB"
                        />
                      </td>
                      <td className="px-3 py-2.5">
                        <Input
                          value={ramVal}
                          onChange={(e) => handleEditChange(p.product_id, "ram", e.target.value)}
                          className="h-8 text-xs min-w-[65px]"
                          placeholder="12GB"
                        />
                      </td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1.5">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleAutoParseRow(p)}
                            title="Auto-extract from SAP Desc"
                            className="h-7 w-7 p-0 text-blue-600 hover:bg-blue-50"
                          >
                            <Sparkles size={13} />
                          </Button>
                          <Button
                            size="sm"
                            variant={isRowEdited ? "default" : "outline"}
                            className="h-7 text-xs px-2.5"
                            disabled={!isRowEdited || saveMutation.isPending}
                            onClick={() => handleSingleSave(p)}
                          >
                            <Save size={12} className="mr-1" />
                            Save
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination Toolbar */}
        {filtered.length > PAGE_SIZE && (
          <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between bg-slate-50">
            <div className="text-xs text-slate-500">
              Showing <span className="font-medium">{(page - 1) * PAGE_SIZE + 1}</span> to <span className="font-medium">{Math.min(page * PAGE_SIZE, filtered.length)}</span> of <span className="font-medium">{filtered.length}</span> products (100 per page)
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="h-8 text-xs"
              >
                Previous
              </Button>
              <span className="text-xs text-slate-600 px-2 font-medium">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="h-8 text-xs"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
